/* ==========================================================================
   adminService

   Platform-level, not estate-level — this is the one place in the app
   that deliberately reaches across every estate, because it's the one
   capability that has to: a super_admin needs to be able to find and
   promote a person regardless of which estate they belong to.

   REAL MODE: lookupUserByEmail reads the `profiles` table under the
   profiles_select_super_admin RLS policy (see supabase/schema.sql), which
   only super_admin accounts satisfy — a plain estate_admin calling this
   would get nothing back for anyone outside their own estate, RLS-blocked
   the same way withdrawals are. promoteUser calls the promote_user
   Postgres function, which independently re-checks the caller is a
   super_admin before changing anything — same pattern as every other
   privileged write in this app (fund_wallet, withdraw_wallet, etc.): the
   frontend gating that hides this screen from non-super-admins is a
   convenience, the database check is the actual boundary.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { fetchProfileRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay } from "./mock/mockUtils";
import type { AuthUser, PromoteUserRequest, UserRole } from "../types";

const VALID_ROLES: UserRole[] = ["user", "estate_admin", "super_admin"];

async function lookupUserByEmail(email: string): Promise<AuthUser | null> {
  if (config.useMockServices) {
    await delay(400);
    const record = mockDb.findUserByEmail(email);
    return record ? mockDb.toAuthUser(record) : null;
  }

  const { data, error } = await getSupabase()
    .from("profiles")
    .select("id")
    .eq("email", email.trim().toLowerCase())
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return fetchProfileRow(data.id);
}

async function promoteUser(req: PromoteUserRequest): Promise<AuthUser> {
  if (!VALID_ROLES.includes(req.newRole)) {
    throw new Error("Not a valid role.");
  }

  if (config.useMockServices) {
    await delay(600);
    const record = mockDb.updateUserRole(req.email, req.newRole);
    if (!record) throw new Error("No account found for that email.");
    return mockDb.toAuthUser(record);
  }

  const { data, error } = await getSupabase().rpc("promote_user", {
    p_email: req.email.trim().toLowerCase(),
    p_new_role: req.newRole,
  });
  if (error) throw new Error(error.message);
  return fetchProfileRow((data as { id: string }).id);
}

export const adminService = { lookupUserByEmail, promoteUser };
